import React, { useRef, useState } from "react";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Button,
  MenuItem,
  useTheme,
} from "@mui/material";
import { tokens } from "../theme";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import AddPhotoAlternateOutlinedIcon from "@mui/icons-material/AddPhotoAlternateOutlined";
import CustomTextField from "../components/common/Textfield";
import CustomSelect from "../components/common/Select";
import { useSelector } from "react-redux";
import { RootState } from "../store/Index";
import apiService from "../service/Api";
import { ToolAsset } from "./ToolsAssetsDrawer";

interface AddAssetDrawerProps {
  open: boolean;
  onClose: () => void;
  onAssetAdded?: (asset: ToolAsset) => void;
}

const AddAssetDrawer: React.FC<AddAssetDrawerProps> = ({
  open,
  onClose,
  onAssetAdded,
}) => {
  const theme = useTheme();
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const colors = tokens(theme.palette.mode, reduxTheme);

  const [assetName, setAssetName] = useState("");
  const [category, setCategory] = useState("");
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const categoryMap: Record<string, string> = {
    "Brand Assets": "brand_asset",
    Logos: "logo",
    "Lower Thirds": "lower_third",
    Images: "image",
    Videos: "video",
  };

  const inputRef = useRef<HTMLInputElement | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setUploadedFile(file);
    }
  };

  const formatFileSize = (sizeInBytes: number) => {
    if (sizeInBytes < 1024) return `${sizeInBytes} B`;
    if (sizeInBytes < 1024 * 1024)
      return `${(sizeInBytes / 1024).toFixed(1)} KB`;
    return `${(sizeInBytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleCloseDrawer = () => {
    setAssetName("");
    setCategory("");
    setUploadedFile(null);
    onClose();
  };
  const handleSaveAsset = async () => {
    if (!assetName || !category || !uploadedFile) {
      alert("Please fill all fields and upload a file.");
      return;
    }

    const formData = new FormData();
    formData.append("name", assetName);
    formData.append("asset_type", categoryMap[category]);

    if (category === "Logos") {
      formData.append("image", uploadedFile);
    } else {
      formData.append("file", uploadedFile);
    }

    formData.append("description", "");
    formData.append("is_active", "");

    try {
      const response = await apiService.post<ToolAsset>(
        "/api/configurations/tools-assets/",
        formData,
      );

      console.log("Asset uploaded successfully:", response.data);

      // ✅ THIS IS WHERE IT GOES
      if (onAssetAdded) {
        onAssetAdded(response.data);
      }

      handleCloseDrawer();
    } catch (error: unknown) {
      if (error instanceof Error) {
        console.error("Failed to upload asset:", error.message);
      } else {
        console.error("Failed to upload asset:", error);
      }
      alert("Failed to upload asset.");
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleCloseDrawer}
      PaperProps={{
        sx: {
          width: { xs: "80%", sm: 400 },
          p: 3,
          backgroundColor: colors.neutral[900],
          color: colors.primary[700],
        },
      }}
    >
      <IconButton
        sx={{
          width: 30,
          height: 30,
          borderRadius: 1,
          bgcolor: colors.primary[900],
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
        onClick={handleCloseDrawer}
      >
        <ArrowBackIosIcon sx={{ fontSize: 20 }} />
      </IconButton>

      <Typography
        variant="h6"
        fontWeight="bold"
        sx={{ mb: 3, mt: 2, fontSize: 24 }}
      >
        Add New Asset
      </Typography>

      <CustomTextField
        type="text"
        value={assetName}
        onChange={(e) => setAssetName(e.target.value)}
        placeholder="Asset Name"
        sx={{ mb: 2 }}
      />

      <CustomSelect
        label="Select Category"
        value={category}
        onChange={(e) => setCategory(e.target.value as string)}
        sx={{ mb: 2 }}
      >
        <MenuItem value="Brand Assets">Brand Assets</MenuItem>
        <MenuItem value="Logos">Logos</MenuItem>
        <MenuItem value="Lower Thirds">Lower Thirds</MenuItem>
        <MenuItem value="Images">Images</MenuItem>
        <MenuItem value="Videos">Videos</MenuItem>
      </CustomSelect>

      <Box
        sx={{
          width: "100%",
          height: 200,
          border: `2px solid ${colors.neutral[800]}`,
          borderRadius: 3,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          mb: 2,
          textAlign: "center",
          "&:hover": { opacity: 0.8 },
        }}
        onClick={() => inputRef.current?.click()}
      >
        <IconButton aria-label="Tool assets">
          <AddPhotoAlternateOutlinedIcon sx={{ fontSize: 38 }} />
        </IconButton>
        <Typography variant="body2">Click or drag to upload a file</Typography>

        <Button
          variant="outlined"
          sx={{
            textTransform: "none",
            mt: 2,
            border: `1px solid ${colors.neutral[700]}`,
            color: colors.neutral[700],
            borderRadius: 2,
          }}
          onClick={(e) => {
            e.stopPropagation();
            inputRef.current?.click();
          }}
        >
          Browse Files
        </Button>
      </Box>

      <input
        type="file"
        ref={inputRef}
        style={{ display: "none" }}
        accept="image/*,video/*"
        onChange={handleFileChange}
      />

      {uploadedFile && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            p: 1,
            mb: 1,
            border: `1px solid ${colors.primary[700]}`,
            borderRadius: 1,
            height: 60,
            backgroundColor: colors.neutral[900],
          }}
        >
          <Box
            sx={{
              width: 30,
              height: 30,
              borderRadius: 1,
              backgroundColor: "rgba(8, 172, 30, 0.6)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mr: 3,
              fontSize: "0.75rem",
            }}
          >
            {uploadedFile.name.split(".").pop()?.toUpperCase()}
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", mt: 2 }}>
            <Typography variant="body2" fontWeight={600}>
              {uploadedFile.name}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: colors.neutral[700],
                display: "block",
                mb: 2,
                opacity: 0.7,
              }}
            >
              {formatFileSize(uploadedFile.size)} of{" "}
              {formatFileSize(uploadedFile.size)}
            </Typography>
          </Box>
        </Box>
      )}

      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Button
          sx={{
            borderRadius: 2,
            mt: 6,
            color: colors.primary[900],
            backgroundColor: colors.primary[400],
            width: 120,
          }}
          onClick={handleSaveAsset}
        >
          Save
        </Button>
      </Box>
    </Drawer>
  );
};

export default AddAssetDrawer;
